import {
  HOME_SECTIONS,
  SECTION_ICONS,
  homeSectionCatalog,
  homeSectionDefinition,
  newSectionConfig,
  readSectionItems,
  type HomeLayoutBlock,
  type HomeSectionCatalog,
  type HomeLayoutProblem,
  type HomeLayoutProblemCode,
  type HomeSectionDefinition,
  type HomeSectionField,
  type HomeSectionGroup,
  type HomeSectionItems,
  type SectionIcon,
} from '@ecom/contracts';
import type { AdminLocale, BilingualLabel } from '@/i18n';
import { newBlockId } from '@/lib/description-blocks';

/**
 * The home page's sections as an operator reads them (ADR-056).
 *
 * What each section holds is the contracts' catalog, shared with the API and
 * the storefront; what it is *called* lives here, because only the admin shows
 * it. Labels are by field name and shared across sections — a heading reads
 * "Heading" wherever it is — with a type's own wording where the shared one
 * would mislead. `home-sections.spec.ts` fails when the catalog grows a type,
 * field or option this file has no words for.
 */

export interface SectionCopy {
  name: BilingualLabel;
  hint: BilingualLabel;
}

export const SECTION_COPY: Record<string, SectionCopy> = {
  banner: {
    name: { ar: 'شريط إعلاني', en: 'Announcement bar' },
    hint: { ar: 'سطر واحد في أعلى الصفحة — توصيل مجاني، عرض، موعد', en: 'One line across the page — free delivery, a sale, a date' },
  },
  hero: {
    name: { ar: 'الواجهة الرئيسية', en: 'Hero' },
    hint: { ar: 'عنوان الصفحة مع صورة وزرّين', en: 'The page’s headline, with a photograph and two buttons' },
  },
  slideshow: {
    name: { ar: 'عرض شرائح', en: 'Slideshow' },
    hint: { ar: 'عدة بانرات تتبدّل، لكل منها نصّها ورابطها', en: 'Banners that slide, each with its own words and link' },
  },
  'image-banner': {
    name: { ar: 'بانر صورة', en: 'Image banner' },
    hint: { ar: 'صورة عريضة واحدة، مع نص وزر إن أردت', en: 'One wide photograph, with words and a button if you like' },
  },
  'promo-grid': {
    name: { ar: 'بطاقات صور', en: 'Image tiles' },
    hint: { ar: 'صور متجاورة، كل واحدة رابط — للأقسام أو العروض', en: 'Photographs side by side, each a link — to categories or offers' },
  },
  'product-rail': {
    name: { ar: 'منتجات', en: 'Products' },
    hint: {
      ar: 'شريط أو شبكة من قسم، الأكثر مبيعًا، الأحدث، أو منتجات تختارها',
      en: 'A carousel or grid from a category, best sellers, new arrivals, or products you pick',
    },
  },
  'featured-collection': {
    name: { ar: 'قسم مميّز', en: 'Featured category' },
    hint: { ar: 'منتجات قسم واحد تحت اسمه', en: 'One category’s products under its name' },
  },
  categories: {
    name: { ar: 'الأقسام', en: 'Categories' },
    hint: { ar: 'بطاقات للأقسام التي تختارها، أو الأقسام الرئيسية كلها', en: 'Tiles for the categories you choose, or every top-level one' },
  },
  features: {
    name: { ar: 'مميزات المتجر', en: 'Store promises' },
    hint: { ar: 'أيقونات مع سطر لكلٍّ منها — التوصيل، الدفع عند الاستلام، الإرجاع', en: 'Icons with a line each — delivery, cash on delivery, returns' },
  },
  'content-block': {
    name: { ar: 'نص', en: 'Text' },
    hint: { ar: 'عنوان وفقرة، مع زر إن أردت', en: 'A heading and a paragraph, with a button if you like' },
  },
  looks: {
    name: { ar: 'التنسيقات', en: 'Shop the look' },
    hint: { ar: 'تنسيقات المتجر، يُضاف كلٌّ منها إلى السلة بخطوة', en: 'The store’s looks, each added to the basket in one step' },
  },
  newsletter: {
    name: { ar: 'النشرة البريدية', en: 'Newsletter' },
    hint: { ar: 'مربع اشتراك في رسائل المتجر', en: 'A box to sign up for the store’s emails' },
  },
};

export const GROUP_COPY: Record<HomeSectionGroup, BilingualLabel> = {
  banners: { ar: 'البانرات', en: 'Banners' },
  products: { ar: 'المنتجات', en: 'Products' },
  categories: { ar: 'الأقسام', en: 'Categories' },
  content: { ar: 'نصوص وأخرى', en: 'Text and more' },
  // Named after the theme when it says its name (ADR-057).
  theme: { ar: 'من القالب', en: 'From your theme' },
};

export const GROUP_ORDER: readonly HomeSectionGroup[] = ['banners', 'products', 'categories', 'content', 'theme'];

/** The core's catalog, for callers that have no theme to merge. */
export const CORE_SECTIONS: HomeSectionCatalog = homeSectionCatalog();

export interface FieldCopy {
  label: BilingualLabel;
  hint?: BilingualLabel;
}

/** Field words by name, the same in every section that has the field. */
const FIELD_COPY: Record<string, FieldCopy> = {
  eyebrow: { label: { ar: 'سطر فوق العنوان', en: 'Line above the heading' }, hint: { ar: 'قصير — «جديد» أو «رمضان ١٤٤٨»', en: 'Short — "New" or "Ramadan 2027"' } },
  title: { label: { ar: 'العنوان', en: 'Heading' } },
  subtitle: { label: { ar: 'العنوان الفرعي', en: 'Subheading' } },
  text: { label: { ar: 'النص', en: 'Text' } },
  body: { label: { ar: 'الفقرة', en: 'Paragraph' } },
  href: { label: { ar: 'الرابط', en: 'Link' } },
  ctaLabel: { label: { ar: 'نص الزر', en: 'Button text' }, hint: { ar: 'اتركه فارغًا لإخفاء الزر', en: 'Leave empty for no button' } },
  ctaHref: { label: { ar: 'رابط الزر', en: 'Button link' } },
  secondaryLabel: { label: { ar: 'نص الزر الثاني', en: 'Second button text' } },
  secondaryHref: { label: { ar: 'رابط الزر الثاني', en: 'Second button link' } },
  imageUrl: { label: { ar: 'الصورة', en: 'Photograph' } },
  mobileImageUrl: {
    label: { ar: 'صورة للجوال', en: 'Photograph for phones' },
    hint: { ar: 'اختيارية — صورة طولية تُعرض على الشاشات الصغيرة', en: 'Optional — an upright picture for small screens' },
  },
  image: { label: { ar: 'الصورة', en: 'Photograph' } },
  mobileImage: {
    label: { ar: 'صورة للجوال', en: 'Photograph for phones' },
    hint: { ar: 'اختيارية — صورة طولية تُعرض على الشاشات الصغيرة', en: 'Optional — an upright picture for small screens' },
  },
  alt: {
    label: { ar: 'وصف الصورة', en: 'Describe the photograph' },
    hint: { ar: 'لقارئ الشاشة ومحركات البحث — ما يظهر فيها في جملة', en: 'For screen readers and search — what it shows, in a sentence' },
  },
  autoplay: { label: { ar: 'تبديل تلقائي', en: 'Play automatically' }, hint: { ar: 'يتوقف عند مرور المؤشر وللزوار الذين يفضّلون تقليل الحركة', en: 'Pauses on hover, and for visitors who prefer less motion' } },
  interval: { label: { ar: 'ثوانٍ لكل شريحة', en: 'Seconds per slide' } },
  height: { label: { ar: 'الارتفاع', en: 'Height' } },
  align: { label: { ar: 'موضع النص', en: 'Words placed' } },
  tone: { label: { ar: 'لون النص', en: 'Words' } },
  columns: { label: { ar: 'الأعمدة', en: 'Columns' } },
  aspect: { label: { ar: 'شكل البطاقة', en: 'Tile shape' } },
  source: { label: { ar: 'المنتجات', en: 'Products from' } },
  categorySlug: { label: { ar: 'القسم', en: 'Category' } },
  sort: { label: { ar: 'الترتيب', en: 'Order' } },
  productIds: {
    label: { ar: 'المنتجات المختارة', en: 'Picked products' },
    hint: { ar: 'بالترتيب الذي تظهر به. المؤرشف منها لا يظهر', en: 'In the order they show. Archived ones drop out' },
  },
  limit: { label: { ar: 'العدد', en: 'How many' } },
  layout: { label: { ar: 'طريقة العرض', en: 'Layout' } },
  viewAll: { label: { ar: 'إظهار «عرض الكل»', en: 'Show "View all"' } },
  categorySlugs: {
    label: { ar: 'الأقسام', en: 'Categories' },
    hint: { ar: 'بالترتيب الذي تظهر به. فارغ = الأقسام الرئيسية بترتيب المتجر', en: 'In the order they show. Empty shows the top-level ones, in the store’s order' },
  },
  showCounts: { label: { ar: 'إظهار عدد المنتجات', en: 'Show how many products' } },
  icon: { label: { ar: 'الأيقونة', en: 'Icon' } },
};

/** Where a section needs its own words for a shared field. */
const FIELD_COPY_BY_TYPE: Record<string, Record<string, FieldCopy>> = {
  banner: { text: { label: { ar: 'نص الشريط', en: 'Announcement' } } },
  // The core hero always has its button: empty words read "Shop now".
  hero: {
    ctaLabel: { label: { ar: 'نص الزر', en: 'Button text' }, hint: { ar: 'فارغ = «تسوّق الآن»', en: 'Empty reads “Shop now”' } },
    title: { label: { ar: 'العنوان', en: 'Heading' }, hint: { ar: 'عنوان الصفحة الرئيسي، ويقرؤه محرك البحث', en: 'The page’s main heading — search engines read it too' } },
  },
  features: { text: { label: { ar: 'سطر تحته', en: 'Line under it' } } },
  newsletter: { text: { label: { ar: 'سطر تحت العنوان', en: 'Line under the heading' } } },
  categories: { limit: { label: { ar: 'عدد الأقسام', en: 'How many categories' }, hint: { ar: 'حين لا تختار أقسامًا بعينها', en: 'When no categories are chosen' } } },
  looks: { limit: { label: { ar: 'عدد التنسيقات', en: 'How many looks' } } },
  'product-rail': { limit: { label: { ar: 'عدد المنتجات', en: 'How many products' } } },
  'featured-collection': { limit: { label: { ar: 'عدد المنتجات', en: 'How many products' } } },
};

/** Option words by field, then value. */
export const OPTION_COPY: Record<string, Record<string, BilingualLabel>> = {
  height: {
    small: { ar: 'منخفض', en: 'Short' },
    medium: { ar: 'متوسط', en: 'Medium' },
    large: { ar: 'مرتفع', en: 'Tall' },
  },
  align: {
    start: { ar: 'البداية', en: 'Start' },
    center: { ar: 'الوسط', en: 'Centre' },
    end: { ar: 'النهاية', en: 'End' },
  },
  tone: {
    light: { ar: 'فاتح — على صورة داكنة', en: 'Light — on a dark photo' },
    dark: { ar: 'داكن — على صورة فاتحة', en: 'Dark — on a light photo' },
  },
  columns: { '2': { ar: '٢', en: '2' }, '3': { ar: '٣', en: '3' }, '4': { ar: '٤', en: '4' } },
  aspect: {
    portrait: { ar: 'طولي', en: 'Tall' },
    square: { ar: 'مربّع', en: 'Square' },
    landscape: { ar: 'عرضي', en: 'Wide' },
  },
  source: {
    category: { ar: 'من قسم', en: 'A category' },
    picked: { ar: 'مختارة يدويًا', en: 'Hand-picked' },
    bestsellers: { ar: 'الأكثر مبيعًا', en: 'Best sellers' },
    top_rated: { ar: 'الأعلى تقييمًا', en: 'Top rated' },
    newest: { ar: 'وصل حديثًا', en: 'New arrivals' },
    price_asc: { ar: 'الأقل سعرًا', en: 'Lowest price' },
  },
  sort: {
    featured: { ar: 'ترتيب القسم', en: 'The category’s own order' },
    newest: { ar: 'الأحدث', en: 'Newest' },
    bestselling: { ar: 'الأكثر مبيعًا', en: 'Best selling' },
    rating: { ar: 'الأعلى تقييمًا', en: 'Top rated' },
    price_asc: { ar: 'الأقل سعرًا', en: 'Lowest price' },
    price_desc: { ar: 'الأعلى سعرًا', en: 'Highest price' },
  },
  layout: {
    carousel: { ar: 'شريط يُسحب', en: 'Carousel' },
    grid: { ar: 'شبكة', en: 'Grid' },
  },
};

/** A list inside a section: what one of it is called, and how to add one. */
export const ITEM_COPY: Record<string, { one: BilingualLabel; add: BilingualLabel }> = {
  slides: { one: { ar: 'شريحة', en: 'Slide' }, add: { ar: 'إضافة شريحة', en: 'Add slide' } },
  tiles: { one: { ar: 'بطاقة', en: 'Tile' }, add: { ar: 'إضافة بطاقة', en: 'Add tile' } },
  features: { one: { ar: 'ميزة', en: 'Promise' }, add: { ar: 'إضافة ميزة', en: 'Add promise' } },
};

export const ICON_COPY: Record<SectionIcon, BilingualLabel> = {
  truck: { ar: 'توصيل', en: 'Delivery' },
  return: { ar: 'إرجاع', en: 'Returns' },
  shield: { ar: 'أمان', en: 'Secure' },
  cash: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
  card: { ar: 'بطاقة', en: 'Card' },
  gift: { ar: 'هدية', en: 'Gift' },
  star: { ar: 'جودة', en: 'Quality' },
  chat: { ar: 'دعم', en: 'Support' },
  clock: { ar: 'سرعة', en: 'Fast' },
  tag: { ar: 'سعر', en: 'Price' },
  scissors: { ar: 'تفصيل', en: 'Tailoring' },
  box: { ar: 'تغليف', en: 'Packaging' },
};

/** The flags a section needs, named as the Features settings page names them. */
export const FLAG_COPY: Record<string, BilingualLabel> = {
  looks: { ar: 'التنسيقات', en: 'Looks' },
  newsletter: { ar: 'النشرة البريدية', en: 'Newsletter' },
};

/**
 * A field's words: a theme's own field carries them (ADR-057); a core field's
 * are this file's. A theme may word a core field too, and its words then win.
 */
export function fieldCopy(type: string, name: string, field?: HomeSectionField): FieldCopy {
  if (field?.label) return { label: field.label, hint: field.hint };
  return FIELD_COPY_BY_TYPE[type]?.[name] ?? FIELD_COPY[name] ?? { label: { ar: name, en: name } };
}

export function optionLabel(field: string, value: string, locale: AdminLocale, definition?: HomeSectionField): string {
  return definition?.optionLabels?.[value]?.[locale] ?? OPTION_COPY[field]?.[value]?.[locale] ?? value;
}

/**
 * A section's name: the core's copy, the theme's own label, and — for a type
 * nobody described — its slug made readable ("rtex-marquee" → "Rtex marquee").
 */
export function sectionName(type: string, locale: AdminLocale, catalog: HomeSectionCatalog = CORE_SECTIONS): string {
  const copy = SECTION_COPY[type];
  if (copy) return copy.name[locale];
  const label = catalog.get(type)?.label;
  if (label) return label[locale];
  const words = type.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What a section is for, in a line; null for a type nobody described. */
export function sectionHint(type: string, locale: AdminLocale, catalog: HomeSectionCatalog = CORE_SECTIONS): string | null {
  return SECTION_COPY[type]?.hint[locale] ?? catalog.get(type)?.hint?.[locale] ?? null;
}

/** What one of a section's items is called, and the button that adds one. */
export function itemCopy(items: HomeSectionItems, locale: AdminLocale): { one: string; add: string } {
  const copy = ITEM_COPY[items.name];
  return {
    one: items.label?.[locale] ?? copy?.one[locale] ?? items.name,
    add: items.add?.[locale] ?? copy?.add[locale] ?? items.name,
  };
}

// ─── Sections in the editor ──────────────────────────────────────────────────

/** A section as the editor holds it: always with an id, whatever the stored row had. */
export type EditorSection = HomeLayoutBlock & { id: string };

/**
 * Ids for a stored layout's sections, stable across renders.
 *
 * A layout saved before sections had ids has none. Its sections get one from
 * their type and place, so the editor can tell them apart without the page
 * counting as changed the moment it opens; they are stored on the next save.
 */
export function withSectionIds(blocks: readonly HomeLayoutBlock[]): EditorSection[] {
  const taken = new Set(blocks.map((block) => block.id).filter((id): id is string => typeof id === 'string'));
  return blocks.map((block, index) => {
    if (typeof block.id === 'string' && block.id) return { ...block, id: block.id };
    let id = `${block.type}-${index + 1}`.slice(0, 40);
    for (let n = 2; taken.has(id); n += 1) id = `${block.type}-${index + 1}-${n}`.slice(0, 40);
    taken.add(id);
    return { ...block, id };
  });
}

/** What "Add section" puts on the page: switched on, with its catalog defaults. */
export function newSection(type: string, catalog: HomeSectionCatalog = CORE_SECTIONS): EditorSection {
  return { id: newBlockId(), type, enabled: true, sortOrder: 0, config: newSectionConfig(type, catalog) };
}

/** A copy with its own id, sharing nothing with the original. */
export function copySection(section: EditorSection): EditorSection {
  return { ...(JSON.parse(JSON.stringify(section)) as EditorSection), id: newBlockId() };
}

/** Positions written back from the order on screen — what the storefront sorts by. */
export function renumber(sections: readonly EditorSection[]): EditorSection[] {
  return sections.map((section, index) => ({ ...section, sortOrder: index + 1 }));
}

/** Why a type cannot be added now, or null when it can. */
export function addBlocker(
  type: string,
  sections: readonly HomeLayoutBlock[],
  flags: Record<string, boolean>,
  maxSections: number,
  catalog: HomeSectionCatalog = CORE_SECTIONS,
): { reason: 'full' | 'perPage' | 'flag'; limit?: number; flag?: string } | null {
  const definition = homeSectionDefinition(type, catalog);
  if (sections.length >= maxSections) return { reason: 'full', limit: maxSections };
  if (definition?.maxPerPage !== undefined && sections.filter((section) => section.type === type).length >= definition.maxPerPage) {
    return { reason: 'perPage', limit: definition.maxPerPage };
  }
  if (definition?.flag && flags[definition.flag] !== true) return { reason: 'flag', flag: definition.flag };
  return null;
}

/** The copy in one language, else the other: a summary is for finding a section, not proofreading it. */
function words(value: unknown, locale: AdminLocale): string | null {
  if (typeof value === 'string') return value.trim() || null;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const pair = value as Record<string, unknown>;
    const other = locale === 'ar' ? 'en' : 'ar';
    for (const language of [locale, other]) {
      const text = pair[language];
      if (typeof text === 'string' && text.trim()) return text.trim();
    }
  }
  return null;
}

/**
 * What a folded section says about itself: its heading, else what it draws
 * from — "Best sellers", "3 slides" — so three product rails are three
 * different rows, not three "Products".
 */
export function sectionSummary(section: HomeLayoutBlock, locale: AdminLocale, catalog: HomeSectionCatalog = CORE_SECTIONS): string | null {
  const definition = homeSectionDefinition(section.type, catalog);
  if (!definition) return null;
  const config = section.config ?? {};
  const heading = definition.summary ? words(config[definition.summary], locale) : null;
  if (heading) return heading;
  if (section.type === 'product-rail') {
    const source = typeof config.source === 'string' ? config.source : 'newest';
    const category = source === 'category' && typeof config.categorySlug === 'string' ? ` · ${config.categorySlug}` : '';
    return `${optionLabel('source', source, locale)}${category}`;
  }
  if (definition.items) {
    const first = readSectionItems(config, definition.items).map((item) => words(item.title, locale)).find(Boolean);
    if (first) return first;
  }
  return null;
}

/** How many items a section holds, when it holds a list. */
export function sectionItemCount(section: HomeLayoutBlock, catalog: HomeSectionCatalog = CORE_SECTIONS): number | null {
  const items = homeSectionDefinition(section.type, catalog)?.items;
  if (!items) return null;
  return readSectionItems(section.config ?? {}, items).length;
}

// ─── Links ───────────────────────────────────────────────────────────────────

export type LinkKind = 'none' | 'category' | 'product' | 'page' | 'products' | 'custom';

/** What a stored path points at — the reverse of what the picker writes. */
export function linkKindOf(value: string): { kind: LinkKind; target: string } {
  if (!value) return { kind: 'none', target: '' };
  if (value === '/products') return { kind: 'products', target: '' };
  const match = /^\/(categories|products|pages)\/([^/?#]+)$/.exec(value);
  if (match) {
    const kind = match[1] === 'categories' ? 'category' : match[1] === 'products' ? 'product' : 'page';
    return { kind, target: decodeURIComponent(match[2]) };
  }
  return { kind: 'custom', target: value };
}

// ─── Problems ────────────────────────────────────────────────────────────────

const PROBLEM_COPY: Record<HomeLayoutProblemCode, BilingualLabel> = {
  too_many_sections: { ar: 'الصفحة تتسع لـ {limit} قسمًا على الأكثر', en: 'A page holds at most {limit} sections' },
  not_a_section: { ar: 'ليس قسمًا صالحًا', en: 'Not a section this store can read' },
  bad_id: { ar: 'معرّف غير صالح', en: 'Its id is not valid' },
  duplicate_id: { ar: 'معرّف مكرر', en: 'Its id is used twice' },
  bad_type: { ar: 'نوع غير صالح', en: 'Its type is not valid' },
  too_many_of_type: { ar: 'الصفحة تتسع لـ {limit} من هذا القسم فقط', en: 'A page holds only {limit} of this section' },
  bad_visibility: { ar: 'الظهور غير صالح', en: 'Its visibility is not valid' },
  bad_schedule: { ar: 'الجدولة: النهاية يجب أن تكون بعد البداية', en: 'Schedule: the end must come after the start' },
  config_too_large: { ar: 'محتواه أطول من المسموح', en: 'It holds more than a section may' },
  required: { ar: '{field} مطلوب', en: '{field} is required' },
  too_long: { ar: '{field} أطول من {limit} حرفًا', en: '{field} is over {limit} characters' },
  out_of_range: { ar: '{field} خارج الحدود المسموحة', en: '{field} is out of range' },
  not_an_option: { ar: '{field}: اختيار غير متاح', en: '{field}: not one of the choices' },
  too_many: { ar: '{field}: {limit} على الأكثر', en: '{field}: at most {limit}' },
  too_few: { ar: '{field}: املأ {limit} على الأقل', en: '{field}: fill in at least {limit}' },
  bad_value: { ar: '{field}: قيمة غير صالحة', en: '{field}: not a valid value' },
  bad_link: { ar: '{field}: رابط داخل المتجر أو عنوان يبدأ بـ https://', en: '{field}: a path on the store, or an address starting https://' },
  bad_image: { ar: '{field}: ارفع الصورة من جديد', en: '{field}: upload the image again' },
  bad_icon: { ar: '{field}: أيقونة غير متاحة', en: '{field}: not one of the icons' },
};

/** A problem as a sentence in the admin's language, naming the field and item the way the form does. */
export function problemMessage(problem: HomeLayoutProblem, locale: AdminLocale, catalog: HomeSectionCatalog = CORE_SECTIONS): string {
  const definition = problem.type ? homeSectionDefinition(problem.type, catalog) : undefined;
  const isItemsList = definition?.items && problem.field === definition.items.name;
  const described = problem.field
    ? (problem.item !== undefined ? definition?.items?.fields : definition?.fields)?.find((field) => field.name === problem.field)
    : undefined;
  const field = problem.field
    ? isItemsList && definition?.items
      ? itemCopy(definition.items, locale).one
      : fieldCopy(problem.type ?? '', problem.field, described).label[locale]
    : '';
  const sentence = PROBLEM_COPY[problem.code][locale]
    .replace('{field}', field)
    .replace('{limit}', problem.limit !== undefined ? String(problem.limit) : '');
  if (problem.item === undefined || !definition?.items) return sentence;
  return `${itemCopy(definition.items, locale).one} ${problem.item + 1} — ${sentence}`;
}

// ─── Coverage, for the spec ──────────────────────────────────────────────────

/** Every copy key the catalog needs: types, fields, options, item lists and icons. */
export function missingCopy(): string[] {
  const missing: string[] = [];
  const check = (definition: HomeSectionDefinition, field: HomeSectionField) => {
    if (!FIELD_COPY_BY_TYPE[definition.type]?.[field.name] && !FIELD_COPY[field.name]) missing.push(`field ${definition.type}.${field.name}`);
    if (field.kind === 'select') {
      for (const option of field.options ?? []) if (!OPTION_COPY[field.name]?.[option]) missing.push(`option ${field.name}.${option}`);
    }
  };
  for (const definition of HOME_SECTIONS) {
    if (!SECTION_COPY[definition.type]) missing.push(`type ${definition.type}`);
    if (!GROUP_COPY[definition.group]) missing.push(`group ${definition.group}`);
    if (definition.flag && !FLAG_COPY[definition.flag]) missing.push(`flag ${definition.flag}`);
    for (const field of definition.fields) check(definition, field);
    if (definition.items) {
      if (!ITEM_COPY[definition.items.name]) missing.push(`items ${definition.items.name}`);
      for (const field of definition.items.fields) check(definition, field);
    }
  }
  for (const icon of SECTION_ICONS) if (!ICON_COPY[icon]) missing.push(`icon ${icon}`);
  return missing;
}
